import { industryOption } from "../../constants.js";

/** Small formats where subline/image may be hidden (SPEC-WAVE2 §3). */
export const SMALL_FORMATS = ["320x50", "728x90", "320x100"];

/**
 * Deterministic per-template+size char budget for a text slot, derived from
 * its box geometry (avg glyph ≈ 0.52em, line height 1.15).
 */
export function slotCharBudget(slot) {
  const fontSize = slot.fontSize ?? 14;
  const perLine = Math.max(4, Math.floor(slot.w / (fontSize * 0.52)));
  const lines = Math.max(1, Math.floor(slot.h / (fontSize * 1.15)));
  return perLine * lines;
}

/** True when the brand's industry forces a locked compliance line. */
export function isRegulated(identity) {
  return industryOption(identity.industry)?.regulated === true;
}

/**
 * The compliance line for a doc. A brand-set compliance line wins;
 * otherwise a regulated industry would inject its canonical line via a
 * language-pack lookup — deferred along with the rest of the mock copy
 * provider (see createInitialBannerDoc's headline note). For now,
 * regulated brands with no custom complianceText get null rather than a
 * generated line.
 */
export function canonicalCompliance(identity) {
  const custom = identity.complianceText?.trim();
  if (custom) return custom;
  // TODO: once the mock copy provider (or real AI generation) is ported,
  // a regulated industry with no custom line should get a generated
  // canonical compliance line here instead of null.
  return null;
}

/**
 * Builds the starting BannerDoc for a newly created asset.
 *
 * headline uses the plain fallback (offer title, truncated to 26 chars) —
 * the original's mock copy provider (language-pack headline generation +
 * a banned-word regeneration loop) was deliberately not ported; this gets
 * replaced by real AI generation later regardless.
 */
export function createInitialBannerDoc({
  template,
  sizes,
  language,
  identity,
  references,
  offerTitle,
  clickUrl,
}) {
  const supportedSizes = sizes.filter((s) => template.supportedSizes.includes(s));
  const logo = (references ?? []).find((r) => r.kind === "logo");
  const palette = identity.palette ?? [];
  const headline = offerTitle.slice(0, 26);

  return {
    templateId: template.id,
    templateVersion: template.version ?? 1,
    sizes: supportedSizes,
    language,
    slots: {
      logoRefId: logo?.id ?? "",
      imageSource: null,
      headline,
      subline: null,
      // TODO: language-pack CTA text (ctaFor) not ported — plain default
      // for now, same reasoning as the headline.
      cta: "Shop now",
      complianceLine: canonicalCompliance(identity),
      complianceFontSize: identity.complianceFontSize || undefined,
      bgColor: palette[0] ?? "#eef1fe",
      ctaColor: palette[1] ?? palette[0] ?? "#4f46e5",
      textColor: "#101828",
    },
    animationPreset: "fade_sequence",
    clickUrl,
    overrides: {},
  };
}